/* eslint-disable @typescript-eslint/no-explicit-any */
import { FetchShipmentsResponseDto } from "./dtos/fetch-shipments.response.dto";
import { IShipment } from "models/shipment/shipment.model";
import {
  DropDropOffLocationsResponseDto,
  DropDropOffLocationsResponseDtoUPS,
  FetchShipmentResponseDto,
} from "./dtos/fetch-shipment.response.dto";
import { DownloadShipmentRequestDto } from "./dtos/download-shipment-request.dto";
import { DHLPickupCapabilitiesType } from "store/createShipmentSlice/createShipmentSlice.types";
import fetch from "isomorphic-unfetch";
import {
  getApiBaseUrl,
  IFetchPostResponse,
  IFetchResponseSingle,
} from "services/api";
import { fetchAll, fetchSingle } from "services/get";
import { apiPaths, customerApiBase } from "enum/paths.enum";
import { CreateShipmentRequestDto } from "./dtos/create-shipment.request.dto";
import { post } from "services/post";
import { patch } from "services/patch";
import { BulkCancelShipmentsRequestDto } from "./dtos/bulk-cancel-shipments.request.dto";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import { del } from "services/del";
import { IQuotedRate } from "models/quote/quote.model";
import { FetchShipmentsQuery } from "./dtos/fetch-shipments-query";
import {
  DropOffAddress,
  DropOffAddressWithServiceId,
} from "store/createShipmentSlice/createShipmentSlice.types";
import { PickupShipmentRequestDto } from "./dtos/pickup-shipment.request.dto";
import { ISupplier } from "models/supplier/supplier.model";
import {
  ExceptionOwner,
  ExceptionsDashboardResponseDto,
} from "./dtos/exceptions-dashboard.response.dto";
import {
  ExceptionsListResponseDto,
  ExceptionsListView,
} from "./dtos/exceptions-list.response.dto";
import { ExceptionsTrendsResponseDto } from "./dtos/exceptions-trends.response.dto";
import { ImportItemsPromptState } from "./dtos/import-items-prompt.response.dto";

export type PackageSizeHistoryItem = {
  type: number;
  length: number;
  width: number;
  height: number;
  useCount: number;
};

export const fetchPackageSizeHistory = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchResponseSingle<PackageSizeHistoryItem[]>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/package-size-history"
  );
};

export type ShipmentItemDescriptionSuggestion = {
  description: string;
  commodityCode: string | null;
  sku: string | null;
  itemValue: number | null;
  weight: number | null;
  countryOfOrigin: string | null;
  manufacturerDetails: string | null;
  manufacturerIdentifier: string | null;
  standardisedIdentifier: string | null;
  currency: string | null;
};

export type ShipmentItemSkuSuggestion = {
  sku: string;
  description: string | null;
  commodityCode: string | null;
  itemValue: number | null;
  weight: number | null;
  countryOfOrigin: string | null;
  manufacturerDetails: string | null;
  manufacturerIdentifier: string | null;
  standardisedIdentifier: string | null;
  currency: string | null;
};

export const fetchAllTrackingStatuses = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchPostResponse<string[]>> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/allTrackingStatuses"
  );
};

export const fetchAllIntegrationStores = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<
  IFetchPostResponse<
    {
      id: string | null;
      identifier: string;
      integrationType: string | null;
      status: string | null;
    }[]
  >
> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/allIntegrationStores"
  );
};

/** A carrier on the account's whitelist plus the services held under it. */
export type CarrierFilterOption = {
  carrierName: string;
  services: string[];
};

export const fetchAllCarriers = async (
  salesAccountUUID: ISalesAccount["uuid"]
  // A server that predates the nested services returns plain carrier names, so
  // the caller normalizes both shapes.
): Promise<IFetchPostResponse<CarrierFilterOption[] | string[]>> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/allCarriers",
    // Asks for each carrier's services. A server that predates them ignores
    // the flag and returns plain carrier names, which the caller also accepts.
    { filters: { withServices: "true" } }
  );
};

export const fetchAllSkus = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchPostResponse<{ id: string; identifier: string }[]>> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/allIntegrationStores"
  );
};

export const fetchShipmentItemDescriptionSuggestions = async (
  salesAccountUUID: ISalesAccount["uuid"],
  search: string,
  limit = 8
): Promise<IFetchResponseSingle<ShipmentItemDescriptionSuggestion[]>> => {
  const params = new URLSearchParams({
    search,
    limit: String(limit),
  });

  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/item-description-suggestions?" +
      params.toString()
  );
};

export const fetchShipmentItemSkuSuggestions = async (
  salesAccountUUID: ISalesAccount["uuid"],
  search: string,
  limit = 8
): Promise<IFetchResponseSingle<ShipmentItemSkuSuggestion[]>> => {
  const params = new URLSearchParams({
    search,
    limit: String(limit),
  });

  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/item-sku-suggestions?" +
      params.toString()
  );
};

export const logShipmentAutocompleteSelection = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: {
    field: "description" | "sku";
    selectedValue: string;
    searchTerm?: string;
  }
): Promise<IFetchPostResponse<{ logged: boolean }>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/autocomplete-selection",
    values
  );
};

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const CSV_MIME = "text/csv";

// A plain object rather than a union: this project has strict mode off, so
// narrowing a discriminated union is not reliable under next build.
export type ReportFileResult = {
  ok: boolean;
  file?: Blob;
  message?: string;
};

const base64ToBlob = (base64: string, mime: string) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

// Shown when a CSV is asked of a server that only knows the workbook.
export const CSV_UNAVAILABLE_MESSAGE =
  "CSV reports are not available yet. Please try again in a few minutes, or download the Excel version.";

/**
 * Fetches the shipment report as the file itself (workbook, or CSV when
 * values.format is "csv"), streamed through the server, rather than as
 * base64 inside JSON: reports run to hundreds of MB and neither the server
 * nor the browser copes with that as a JSON string. A server that predates
 * the streamed form still answers with JSON, which is converted here, so the
 * two can be deployed in either order.
 */
export const downloadShipmentReportFile = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<ReportFileResult> => {
  const isCsv = values?.format === "csv";

  try {
    const response = await fetch(
      `${getApiBaseUrl()}${customerApiBase}/${salesAccountUUID}${
        apiPaths.shipments
      }/downloadShipmentReport`,
      {
        method: "POST",
        cache: "no-cache",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: isCsv ? CSV_MIME : XLSX_MIME,
          authorization: "Bearer " + window["accessToken"],
        },
        body: JSON.stringify(values ?? {}),
      }
    );

    const contentType = response.headers.get("content-type") ?? "";

    if (!response.ok) {
      const body = contentType.includes("json")
        ? await response.json().catch(() => ({}))
        : {};
      return {
        ok: false,
        message: typeof body?.message === "string" ? body.message : undefined,
      };
    }

    if (contentType.includes("json")) {
      // The JSON form is always the workbook: a server that answers this
      // way does not know the format field, so a CSV cannot be served.
      if (isCsv) {
        return { ok: false, message: CSV_UNAVAILABLE_MESSAGE };
      }

      const { data } = await response.json();
      return data
        ? { ok: true, file: base64ToBlob(data, XLSX_MIME) }
        : { ok: false };
    }

    return { ok: true, file: await response.blob() };
  } catch (err) {
    return { ok: false };
  }
};

export const downloadShipmentReport = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/downloadShipmentReport",
    values
  );
};

export const downloadTemuReport = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/downloadTemuReport",
    values
  );
};

export const downloadLabelReport = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/downloadLabelReport",
    values
  );
};

export const downloadSurchargeReport = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/downloadSurchargeReport",
    values
  );
};

export const downloadShipmentManifest = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: DownloadShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/downloadShipmentManifest",
    values
  );
};

export const createPickup = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: PickupShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/createPickup",
    values
  );
};

export const cancelPickup = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values?: PickupShipmentRequestDto
): Promise<IFetchPostResponse<string>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/cancelPickup",
    values
  );
};

// DHL's actual pickup window for the selected shipments' product/origin/
// destination/date, so the pickup modal can constrain its date/time pickers
// instead of the user finding out only after a failed booking attempt.
// Distinct from services/api/quotes' fetchDHLPickupCapabilities, which looks
// the window up by an in-flight rate quote rather than an already-created
// shipment.
export const fetchPickupDHLCapabilities = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: { shipmentIds: number[]; pickupDate: string }
): Promise<
  IFetchPostResponse<{ capabilities: DHLPickupCapabilitiesType | null }>
> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/fetchDHLPickupCapabilities",
    values
  );
};

export const fetchEligiblePickupShipments = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: FetchShipmentsQuery
): Promise<any> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/fetchEligiblePickup",
    query
  );
};

export const fetchPickupAddresses = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: FetchShipmentsQuery
): Promise<any> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/fetchPickupAddresses",
    query
  );
};

export const fetchPickups = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: FetchShipmentsQuery
): Promise<FetchShipmentsResponseDto> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/fetchPickups",
    query
  );
};

export const fetchShipmentsRelatedToPickups = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: FetchShipmentsQuery
): Promise<FetchShipmentsResponseDto> => {
  return await fetchAll(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/fetchShipmentsRelatedToPickup",
    query
  );
};

export const fetchShipments = async (
  salesAccountUUID: ISalesAccount["uuid"],
  query?: FetchShipmentsQuery,
  controller?: AbortController
): Promise<FetchShipmentsResponseDto> => {
  return await fetchAll(
    customerApiBase + "/" + salesAccountUUID + apiPaths.shipments,
    {
      ...query,
      filters: { ...query.filters },
    },
    controller
  );
};

export const findShipmentForScan = async (
  salesAccountUUID: ISalesAccount["uuid"],
  reference: string
): Promise<IFetchResponseSingle<IShipment>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/scan-lookup?reference=" +
      encodeURIComponent(reference)
  );
};

export const fetchShipment = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"]
): Promise<IFetchResponseSingle<FetchShipmentResponseDto>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID
  );
};

export const fetchShipmentPod = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"]
): Promise<IFetchResponseSingle<{ html: string }>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID +
      "/pod"
  );
};

export const createShipment = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: CreateShipmentRequestDto
): Promise<IFetchPostResponse<FetchShipmentResponseDto>> => {
  return await post(
    customerApiBase + "/" + salesAccountUUID + apiPaths.shipments,
    values
  );
};

export const updateShipment = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"],
  values: {
    createShipmentRequest: CreateShipmentRequestDto;
    duplicateOrEdit: string;
  }
): Promise<IFetchPostResponse<FetchShipmentResponseDto>> => {
  return await patch(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID,
    values
  );
};

export const updateShipmentRate = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"],
  values: {
    rateUUID: IQuotedRate["uuid"];
  }
): Promise<IFetchPostResponse<FetchShipmentResponseDto>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID +
      "/update-rate",
    values
  );
};
export const fetchShipmentDropOffLocations = async (
  values: DropOffAddress,
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchResponseSingle<DropDropOffLocationsResponseDto>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/dropOffLocations",
    values
  );
};
export const fetchShipmentDropOffLocationsUPS = async (
  payload: DropOffAddressWithServiceId,
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchResponseSingle<DropDropOffLocationsResponseDtoUPS>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/dropOffLocationsUPS",
    payload
  );
};
export const deleteShipment = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"],
  apiPath: ISupplier["apiPath"]
): Promise<IFetchPostResponse<FetchShipmentResponseDto>> => {
  const url = `${customerApiBase}/${salesAccountUUID}${apiPaths.shipments}/${shipmentUUID}`;
  return del(url, apiPath !== undefined ? { apiPath } : undefined);
};

// Undoes a cancel made in Connexx. The server refuses (400 with a message)
// when the cancel really reached the carrier or an edit replaced the shipment.
export const uncancelShipment = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"]
): Promise<IFetchPostResponse<FetchShipmentResponseDto>> => {
  return await post(
    `${customerApiBase}/${salesAccountUUID}${apiPaths.shipments}/${shipmentUUID}/uncancel`
  );
};

export const bulkCancelShipments = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: BulkCancelShipmentsRequestDto
): Promise<IFetchPostResponse<any>> => {
  return await post(
    customerApiBase + "/" + salesAccountUUID + apiPaths.bulkCancelShipments,
    values
  );
};

export const rerunRulesShipments = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: {
    shipmentIds: IShipment["id"][];
  }
): Promise<IFetchPostResponse<any>> => {
  return await post(
    customerApiBase + "/" + salesAccountUUID + apiPaths.rerunShipmentRules,
    values
  );
};

export type ParsedInvoiceItem = {
  description: string | null;
  sku: string | null;
  quantity: number | null;
  unitValue: number | null;
  itemValue: number | null;
  currency: string | null;
  commodityCode: string | null;
  countryOfOrigin: string | null;
  manufacturerDetails: string | null;
  manufacturerIdentifier: string | null;
  standardisedIdentifier: string | null;
  weight: number | null;
};

export type ParseCommercialInvoiceResponse = {
  items: ParsedInvoiceItem[];
  summary: {
    invoiceNumber: string | null;
    invoiceDate: string | null;
    vendorName: string | null;
    total: number | null;
    currency: string | null;
  };
};

export const fetchShipmentStats = async (
  salesAccountUUID: ISalesAccount["uuid"],
  startDate: string,
  endDate: string,
  tzOffsetMinutes: number
): Promise<IFetchResponseSingle<Record<string, { date: string; count: number }[]>>> => {
  const params = new URLSearchParams({ startDate, endDate, tzOffsetMinutes: String(tzOffsetMinutes) });
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/ready-to-print-stats?" +
      params.toString()
  );
};

export const parseCommercialInvoice = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: { fileBase64: string; filename?: string }
): Promise<IFetchPostResponse<ParseCommercialInvoiceResponse>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/parse-commercial-invoice",
    values
  );
};

export const fetchImportItemsPrompt = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<IFetchResponseSingle<ImportItemsPromptState>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/import-items-prompt"
  );
};

// Shared by the exceptions endpoints: date window plus the viewer's IANA
// timezone, so the server buckets days by the viewer's local calendar (same
// convention as the Intelligence page).
const exceptionsQueryString = (params: {
  days?: number;
  from?: string;
  to?: string;
}) => {
  const qs = new URLSearchParams();
  qs.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
  if (params.from && params.to) {
    qs.set("from", params.from);
    qs.set("to", params.to);
  } else if (params.days) {
    qs.set("days", String(params.days));
  }
  return qs;
};

export const fetchExceptionsDashboard = async (
  salesAccountUUID: ISalesAccount["uuid"],
  params: { days?: number; from?: string; to?: string }
): Promise<IFetchResponseSingle<ExceptionsDashboardResponseDto>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/exceptions?" +
      exceptionsQueryString(params).toString()
  );
};

export const fetchExceptionsTrends = async (
  salesAccountUUID: ISalesAccount["uuid"],
  params: { days?: number; from?: string; to?: string }
): Promise<IFetchResponseSingle<ExceptionsTrendsResponseDto>> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/exceptions/trends?" +
      exceptionsQueryString(params).toString()
  );
};

export const fetchExceptionsList = async (
  salesAccountUUID: ISalesAccount["uuid"],
  params: {
    view: ExceptionsListView;
    days?: number;
    from?: string;
    to?: string;
    owner?: ExceptionOwner;
    reason?: string;
    carrier?: string;
    search?: string;
    sort?: string;
    dir?: "asc" | "desc";
    offset?: number;
    limit?: number;
  }
): Promise<IFetchResponseSingle<ExceptionsListResponseDto>> => {
  const qs = exceptionsQueryString(params);
  qs.set("view", params.view);
  if (params.owner) qs.set("owner", params.owner);
  if (params.reason) qs.set("reason", params.reason);
  if (params.carrier) qs.set("carrier", params.carrier);
  if (params.search) qs.set("search", params.search);
  if (params.sort) qs.set("sort", params.sort);
  if (params.dir) qs.set("dir", params.dir);
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.limit) qs.set("limit", String(params.limit));
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/exceptions/list?" +
      qs.toString()
  );
};

export type RetryIntegrationUpdateResponse = {
  status: string | null;
  errorCode: string | null;
  attemptCount: number | null;
  lastDispatchedAt: string | null;
};

export const retryIntegrationUpdate = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"]
): Promise<IFetchPostResponse<RetryIntegrationUpdateResponse>> => {
  return await post(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID +
      "/retry-integration-update",
    {}
  );
};

// Lightweight poll read for the drawer's sync status row: returns only the
// integrationUpdate summary, not the full shipment.
export const fetchShipmentIntegrationUpdate = async (
  salesAccountUUID: ISalesAccount["uuid"],
  shipmentUUID: IShipment["uuid"]
): Promise<
  IFetchResponseSingle<{ integrationUpdate: IShipment["integrationUpdate"] }>
> => {
  return await fetchSingle(
    customerApiBase +
      "/" +
      salesAccountUUID +
      apiPaths.shipments +
      "/" +
      shipmentUUID +
      "/integration-update"
  );
};
